import { type CSSProperties, Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@ibas/ui';
import { ChevronLeft, ChevronRight, GraduationCap } from 'lucide-react';

import { SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE, STAGGER } from '@/lib/motion-tokens';

/** A single graduate testimonial rendered in the reel. */
export interface ReelTestimonial {
  /** Full graduate name (e.g. "Dr. Rozaidi Razali"). */
  name: string;
  /** Country / location line shown under the name. */
  location: string;
  /** The verbatim quote. Rendered character-by-character on entrance. */
  quote: string;
}

export interface ScrollReelTestimonialsProps {
  /** Testimonials to cycle through. Order defines the reel sequence. */
  testimonials: ReelTestimonial[];
  /** Section heading. Default "What our graduates say". */
  heading?: string;
  /** Small uppercase eyebrow above the heading. */
  eyebrow?: string;
  /** Dwell time on each testimonial before auto-advancing (ms). Default 6500. */
  autoAdvanceMs?: number;
  className?: string;
}

/** Layout geometry for the counter-rotating portrait ring (px). */
const RING_RADIUS = 116;
const PORTRAIT_SIZE = 92;

/**
 * ScrollReelTestimonials — a "smart" graduate-voices reel.
 *
 * Motion intent: the portraits sit on a wheel that spins forward one seat each
 * time the reel advances (GSAP), while every portrait counter-rotates to stay
 * perfectly upright — a considered, mechanical-yet-luxurious motion. The active
 * quote then rises into place one character at a time (the shared, reduced-motion
 * gated `.scroll-reel-char` keyframe). Under `prefers-reduced-motion: reduce`
 * nothing spins or auto-advances: the wheel snaps to the active seat, the quote
 * is fully legible instantly, and readers steer with the portrait buttons.
 */
export function ScrollReelTestimonials({
  testimonials,
  heading = 'What our graduates say',
  eyebrow = 'Graduate voices',
  autoAdvanceMs = 6500,
  className,
}: ScrollReelTestimonialsProps) {
  const reduced = useReducedMotion();
  const count = testimonials.length;
  const angle = count > 0 ? 360 / count : 0;

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const ringRef = useRef<HTMLDivElement>(null);
  const seatRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rot = useRef(0); // live ring rotation (deg), always accumulating forward
  const prevActive = useRef(0);

  const setSeatRef = useCallback((el: HTMLDivElement | null, i: number) => {
    seatRefs.current[i] = el;
  }, []);

  const go = useCallback((i: number) => setActive(((i % count) + count) % count), [count]);

  // Drive the counter-rotating wheel whenever the active seat changes.
  useGSAP(
    () => {
      const ring = ringRef.current;
      if (!ring || count === 0) return;

      const applyUpright = (v: number) => {
        gsap.set(ring, { rotation: v });
        seatRefs.current.forEach((seat, i) => {
          if (seat) gsap.set(seat, { rotation: -(i * angle) - v });
        });
      };

      if (reduced) {
        rot.current = -active * angle;
        prevActive.current = active;
        applyUpright(rot.current);
        return;
      }

      // Always spin forward by the shortest forward step count.
      const stepsForward = (((active - prevActive.current) % count) + count) % count;
      prevActive.current = active;
      rot.current -= stepsForward * angle;

      const proxy = { v: gsap.getProperty(ring, 'rotation') as number };
      gsap.to(proxy, {
        v: rot.current,
        duration: DURATION.slow,
        ease: GSAP_EASE.out,
        onUpdate: () => applyUpright(proxy.v),
      });
    },
    { scope: ringRef, dependencies: [active, reduced, angle, count] },
  );

  // Auto-advance — never under reduced motion, and pauses on hover/focus.
  useEffect(() => {
    if (reduced || paused || count < 2) return;
    const id = window.setInterval(() => setActive((a) => (a + 1) % count), autoAdvanceMs);
    return () => window.clearInterval(id);
  }, [reduced, paused, count, autoAdvanceMs]);

  const current = testimonials[active];

  return (
    <section
      className={cn('flex min-h-[100svh] items-center bg-muted py-12 md:py-16', className)}
      aria-roledescription="Graduate testimonials"
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            {eyebrow}
          </p>
          <SplitReveal
            as="h2"
            by="words"
            className="mt-3 font-display text-4xl font-semibold text-foreground sm:text-5xl"
          >
            {heading}
          </SplitReveal>
        </div>

        <div
          className="mt-10 grid items-center gap-10 lg:mt-14 lg:grid-cols-[minmax(0,320px)_1fr] lg:gap-16"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          {/* Counter-rotating portrait wheel */}
          <div className="relative mx-auto aspect-square w-[300px] shrink-0">
            <div className="absolute inset-6 rounded-full border border-dashed border-border" />
            <div
              className="pointer-events-none absolute top-1/2 left-1/2 flex size-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-navy text-brand-gold shadow-lg"
              aria-hidden="true"
            >
              <GraduationCap className="size-8" strokeWidth={1.5} />
            </div>

            <div ref={ringRef} className="absolute inset-0">
              {testimonials.map((t, i) => {
                const seatStyle: CSSProperties = {
                  width: PORTRAIT_SIZE,
                  height: PORTRAIT_SIZE,
                  marginLeft: -PORTRAIT_SIZE / 2,
                  marginTop: -PORTRAIT_SIZE / 2,
                  transform: `rotate(${i * angle}deg) translateY(-${RING_RADIUS}px)`,
                };
                const isActive = i === active;
                return (
                  <div key={t.name} className="absolute top-1/2 left-1/2" style={seatStyle}>
                    <div
                      ref={(el) => setSeatRef(el, i)}
                      style={{ transform: `rotate(${-(i * angle)}deg)` }}
                    >
                      <button
                        type="button"
                        onClick={() => go(i)}
                        aria-pressed={isActive}
                        aria-label={`Show testimonial from ${t.name}`}
                        className={cn(
                          'block size-full overflow-hidden rounded-full border-2 transition-[transform,border-color,box-shadow] duration-300 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                          isActive
                            ? 'scale-110 border-brand-gold shadow-lg'
                            : 'scale-90 border-border opacity-70 hover:opacity-100',
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className="flex size-full items-center justify-center bg-brand-navy font-display text-xl font-semibold text-brand-gold"
                        >
                          {initials(t.name)}
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Active quote — rises in character by character */}
          <figure className="text-center lg:text-left">
            <GraduationCap
              className="mx-auto mb-6 size-8 text-brand-gold lg:mx-0"
              aria-hidden="true"
              strokeWidth={1.5}
            />
            <blockquote
              key={active}
              className="font-display text-2xl leading-snug font-medium text-foreground sm:text-3xl"
            >
              <ReelQuote text={current?.quote ?? ''} />
            </blockquote>
            <figcaption className="mt-8">
              <p className="font-display text-lg font-semibold text-foreground">{current?.name}</p>
              <p className="mt-1 font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                {current?.location}
              </p>
            </figcaption>

            {/* Manual navigation: prev/next controls flank the dot row — dots stay intact */}
            <div className="mt-8 flex items-center justify-center gap-3 lg:justify-start">
              <button
                type="button"
                onClick={() => go(active - 1)}
                aria-label="Previous testimonial"
                className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors duration-300 ease-out hover:border-brand-gold hover:text-brand-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <ChevronLeft className="size-5" aria-hidden="true" strokeWidth={1.75} />
              </button>

              <div
                className="flex items-center gap-2"
                role="tablist"
                aria-label="Choose a testimonial"
              >
                {testimonials.map((t, i) => (
                  <button
                    key={t.name}
                    type="button"
                    role="tab"
                    aria-selected={i === active}
                    aria-label={`Testimonial ${i + 1} of ${count}: ${t.name}`}
                    onClick={() => go(i)}
                    className={cn(
                      'h-2 rounded-full transition-all duration-300 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                      i === active
                        ? 'w-8 bg-brand-gold'
                        : 'w-2 bg-border hover:bg-muted-foreground/50',
                    )}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => go(active + 1)}
                aria-label="Next testimonial"
                className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors duration-300 ease-out hover:border-brand-gold hover:text-brand-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <ChevronRight className="size-5" aria-hidden="true" strokeWidth={1.75} />
              </button>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}

/** Two-letter monogram from a graduate's name (an honorific like "Dr." is stripped). */
function initials(name: string): string {
  const parts = name
    .replace(/^(Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, '')
    .trim()
    .split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Splits a quote into upright, non-breaking word groups and renders each glyph
 * as a `.scroll-reel-char` span with a staggered `animationDelay`, so the line
 * rises one character at a time. The CSS keyframe is reduced-motion gated, so
 * opt-out users simply see the finished, static line.
 */
function ReelQuote({ text }: { text: string }) {
  const words = text.split(' ');
  const step = Math.min(STAGGER.tight, DURATION.slow / Math.max(text.length, 1));
  let charIndex = 0;

  return (
    <span>
      &ldquo;
      {words.map((word, wi) => {
        const chars = [...word];
        const group = (
          <span className="inline-block whitespace-nowrap">
            {chars.map((ch, ci) => {
              const delay = charIndex * step;
              charIndex += 1;
              return (
                <span
                  // Positional glyphs of a static quote — index is the stable identity.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${ch}-${ci}`}
                  className="scroll-reel-char"
                  style={{ animationDelay: `${delay.toFixed(3)}s` }}
                >
                  {ch}
                </span>
              );
            })}
          </span>
        );
        return (
          // Positional word groups of a static quote — index is the stable identity.
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`${word}-${wi}`}>
            {group}
            {wi < words.length - 1 ? ' ' : null}
          </Fragment>
        );
      })}
      &rdquo;
    </span>
  );
}

/**
 * The three verified IBAS graduate testimonials (Brand content bank). We hold the
 * verbatim quotes but not photographs of these graduates, so the reel shows an
 * initials monogram rather than a stand-in face — attaching a stranger's portrait
 * to a named person would misrepresent them (see the ibas-brand sourcing rule).
 */
const IBAS_TESTIMONIALS: ReelTestimonial[] = [
  {
    name: 'Dr. Rozaidi Razali',
    location: 'Malaysia',
    quote:
      'At IBAS, the dedicated lecturers and staff helped me meet this challenge with mentorship and constant encouragement.',
  },
  {
    name: 'Dr. Roy Prasad',
    location: 'Malaysia',
    quote:
      'The Doctorate of Business Administration programme improved my personal skills in analysing, criticising and decision-making.',
  },
  {
    name: 'Dr. Prem Kumar',
    location: 'Malaysia',
    quote:
      'An excellent experience studying with IBAS — a perfect combination of great professors and flexible hours of teaching.',
  },
];

/**
 * IBASTestimonials — ready-to-drop section rendering the counter-rotating
 * graduate reel with the three verified IBAS testimonials.
 */
export function IBASTestimonials({ className }: { className?: string }) {
  return <ScrollReelTestimonials testimonials={IBAS_TESTIMONIALS} className={className} />;
}
